import React, { createContext, useContext, useMemo } from 'react';
import { ThemeProvider, useColorScheme } from '@mui/material/styles';
import { appTheme } from './themes/appTheme';
import { CssBaseline } from '@mui/material';

/**
 * 主题上下文接口定义
 */
interface ThemeContextType {
  mode: 'light' | 'dark' | 'system';
  setMode: (mode: 'light' | 'dark' | 'system') => void;
  toggleColorMode: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/**
 * Hook: 获取当前应用主题状态和操作
 */
export const useAppTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useAppTheme must be used within an AppThemeProvider');
  }
  return context;
};

/**
 * 内部同步组件：将 MUI 的 colorScheme 状态暴露给我们的 Context
 * 这样可以避免维护两份状态导致的冲突和闪烁循环
 */
function ThemeContextSync({ children }: { children: React.ReactNode }) {
  const { mode, setMode } = useColorScheme();

  const contextValue = useMemo(() => ({
    mode: (mode || 'system') as 'light' | 'dark' | 'system',
    setMode: (m: 'light' | 'dark' | 'system') => setMuiMode(m, setMode),
    toggleColorMode: () => {
      const nextMode = mode === 'dark' ? 'light' : 'dark';
      setMuiMode(nextMode, setMode);
    }
  }), [mode, setMode]);

  // 辅助函数，确保类型匹配
  function setMuiMode(m: string, setter: (m: any) => void) {
      setter(m);
  }

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * 应用主题提供者
 * 集成了 MUI v6 的 CSS 变量主题方案，并支持深色模式切换和系统跟随
 */
export function AppThemeProvider({ children }: { children: React.ReactNode }) {
  return (
    // 使用 defaultMode="system" 让 MUI 自动处理初始化逻辑和 localStorage 存储
    // 默认存储 key 为 'mui-color-scheme'
    <ThemeProvider theme={appTheme} defaultMode="system">
      <CssBaseline />
      <ThemeContextSync>
        {children}
      </ThemeContextSync>
    </ThemeProvider>
  );
}
